import React, { FC, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from 'buyside/components/presentation/Button/button';
import Modal from 'buyside/components/presentation/Modal/modal';

import EnvelopeIcon from 'buyside/svgs/envelope.svg';

import {
  AllocatorProfileShowPageQuery,
  AllocatorProfileShowPageDocument,
  CreateIntroRequestMutationVariables,
  useCreateIntroRequestMutation
} from 'interfaces/graphql-typings';
export type AdminFundHistoriesType = NonNullable<
  AllocatorProfileShowPageQuery['currentUser']
>['adminFunds'];

import styles from './getIntroducedModalStyles.scss';

const GetIntroducedModal: FC<{
  adminFunds: AdminFundHistoriesType;
  allocatorProfileId: string;
  disabled: boolean;
}> = ({ adminFunds, allocatorProfileId, disabled }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { getValues, register, setValue, watch } = useForm<CreateIntroRequestMutationVariables>();

  const resetMessage = (): void => setValue('message', '', { shouldDirty: false });

  const [createIntroRequest, { loading }] = useCreateIntroRequestMutation({
    variables: {
      allocatorProfileId,
      fundProfileId: getValues('fundProfileId'),
      message: getValues('message')
    },
    update(cache, { data }) {
      const currentUser = data?.createIntroRequest?.currentUser;
      const updatedFundProfile = data?.createIntroRequest?.fundProfile;
      if (!currentUser || !updatedFundProfile) return;

      const cachedQuery = cache.readQuery<AllocatorProfileShowPageQuery>({
        query: AllocatorProfileShowPageDocument,
        variables: { allocatorProfileId: Number(allocatorProfileId) }
      });
      if (!cachedQuery?.currentUser) return;

      cache.writeQuery<AllocatorProfileShowPageQuery>({
        query: AllocatorProfileShowPageDocument,
        data: {
          ...cachedQuery,
          currentUser: {
            ...cachedQuery.currentUser,
            adminFunds: [
              ...cachedQuery.currentUser.adminFunds.map(employerHistory => {
                if (employerHistory.employer?.employerProfile?.id === updatedFundProfile.id) {
                  return {
                    ...employerHistory,
                    employer: {
                      ...employerHistory.employer,
                      employerProfile: {
                        ...employerHistory.employer.employerProfile,
                        ...updatedFundProfile,
                        alreadyRequestedIntro: true
                      }
                    }
                  };
                } else {
                  return employerHistory;
                }
              })
            ]
          }
        },
        variables: { allocatorProfileId }
      });
    },
    onCompleted: () => {
      setIsModalOpen(false);
      toast.success('Your introduction request was sent successfully.', { theme: 'colored' });
      resetMessage();
    },
    onError: error => {
      setIsModalOpen(false);
      toast.error(error.message, { theme: 'colored' });
      resetMessage();
    }
  });

  const selectedFundId = watch('fundProfileId');
  const { alreadyRequestedIntro, introRequestsLeft } =
    useMemo(
      () =>
        selectedFundId
          ? adminFunds.find(
              employerHistory => employerHistory?.employer?.employerProfile?.id === selectedFundId
            )?.employer?.employerProfile
          : adminFunds[0].employer?.employerProfile,
      [adminFunds, selectedFundId]
    ) || {};

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        className={styles.getIntroducedButton}
        disabled={disabled}
        icon={EnvelopeIcon}
        onClick={() => setIsModalOpen(true)}
        size={BUTTON_SIZES.LARGE}
        text="Get Introduced"
        type={BUTTON_TYPES.MAJOR}
        width={BUTTON_WIDTHS.FULL}
      />
    ),
    [disabled]
  );

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      renderOpenModalButton={renderOpenModalButton}
      title="Introduction Request Message"
    >
      <form>
        <p>
          Requesting an introduction will instantly add you to this institution's list of funds
          seeking a direct introduction. If the respective institution agrees to be introduced to
          you, both firms will receive a notification via email indicating as such, thus starting a
          conversation. Consider this feature only if you have serious interest in accepting capital
          from this firm.
        </p>
        From:&nbsp;
        <select id="fundProfileId" className={styles.fundSelect} {...register('fundProfileId')}>
          {adminFunds.map(employerHistory => {
            const employer = employerHistory.employer;
            if (!employer || !employer.employerProfile) return null;
            return (
              <option key={employerHistory.id} value={employer.employerProfile.id}>
                {employer.name}
              </option>
            );
          })}
        </select>
        <textarea
          id="message"
          className={`form-control ${styles.getIntroducedModalTextArea}`}
          disabled={alreadyRequestedIntro || !introRequestsLeft}
          placeholder="Please provide the allocator with context around the nature
              of your request and why it may be relevant."
          {...register('message')}
        />
        {alreadyRequestedIntro ? (
          <strong>
            A request has already been sent to this allocator
            {adminFunds.length > 1 ? ' from this fund' : ''}.
          </strong>
        ) : null}
        {introRequestsLeft ? null : (
          <strong>{adminFunds.length > 1 ? 'This ' : 'Your '}fund has no requests left.</strong>
        )}
      </form>
      <Button
        disabled={alreadyRequestedIntro || !introRequestsLeft || Boolean(!watch('message'))}
        isLoading={loading}
        onClick={createIntroRequest}
        text={loading ? 'Sending...' : 'Request Introduction'}
      />
    </Modal>
  );
};

export default GetIntroducedModal;
